/**
 * Where a rule would have fired: one row per entity across the backtest's
 * range, each episode a mark at its place in time. Answers "one ship or all
 * of them, and when" at a glance - the density strip of the plan's *Backtest*
 * section. Drawn from the watcher's result; nothing is computed here.
 */

import { html, nothing, type TemplateResult } from 'lit';
import type { ArgusBacktestResult } from '../../api/types.ts';

function day(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function stamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Where along [from, to] a moment falls, as a percentage, clamped. */
export function place(iso: string, from: number, to: number): number {
  const t = new Date(iso).getTime();
  return Math.max(0, Math.min(100, ((t - from) / Math.max(1, to - from)) * 100));
}

export function renderBacktestStrip(
  result: ArgusBacktestResult,
  opts: {
    maxRows?: number;
    onPick?: (entity: string, start: string) => void;
    /** The editor's full-width timeline: a count per row, a line each week,
     *  quiet rows dimmed, and the picked episode marked. */
    wide?: boolean;
    picked?: { entity: string; start: string } | null;
  } = {},
): TemplateResult {
  const from = new Date(result.range_from).getTime();
  const to = new Date(result.range_to).getTime();
  const byEntity = new Map<string, ArgusBacktestResult['episodes']>();
  for (const e of result.episodes) {
    const list = byEntity.get(e.entity) ?? [];
    list.push(e);
    byEntity.set(e.entity, list);
  }
  const rows = [...result.entities]
    .filter((r) => !r.error)
    .sort((a, b) => (b.episodes ?? 0) - (a.episodes ?? 0));
  const shown = opts.maxRows ? rows.filter((r) => r.episodes).slice(0, opts.maxRows) : rows;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => new Date(from + f * (to - from)).toISOString());
  const capped = result.totals.episodes > result.episodes.length;
  const weeks: number[] = [];
  if (opts.wide) for (let t = from + 7 * 86400000; t < to; t += 7 * 86400000) weeks.push(((t - from) / (to - from)) * 100);
  const picked = opts.picked;
  return html`<div class="argus-strip ${opts.maxRows ? 'is-compact' : ''} ${opts.wide ? 'is-wide' : ''}">
    ${shown.map(
      (r) => html`<div class="argus-strip-row ${opts.wide && !r.episodes ? 'is-quiet' : ''} ${picked?.entity === r.entity ? 'is-picked' : ''}">
        <span class="argus-strip-name" title=${r.entity}>${r.name}</span>
        <span class="argus-strip-track">
          ${weeks.map((w) => html`<span class="argus-strip-week" style=${`left:${w}%`}></span>`)}
          ${(byEntity.get(r.entity) ?? []).map((e) => {
            const left = place(e.start, from, to);
            const right = place(e.end ?? e.last, from, to);
            return html`<span
              class="argus-strip-mark ${e.end ? '' : 'is-open'} ${picked?.entity === e.entity && picked.start === e.start ? 'is-picked' : ''}"
              style=${`left:${left}%;width:max(2px, ${right - left}%)`}
              title=${`${r.name}: ${stamp(e.start)} - ${e.end ? stamp(e.end) : 'still holding at the end'}`}
              @click=${opts.onPick ? () => opts.onPick?.(e.entity, e.start) : nothing}
            ></span>`;
          })}
        </span>
        ${opts.wide ? html`<span class="argus-strip-count">${r.episodes ? r.episodes : '-'}</span>` : nothing}
      </div>`,
    )}
    <div class="argus-strip-row is-axis" aria-hidden="true">
      <span class="argus-strip-name"></span>
      <span class="argus-strip-track">
        ${ticks.map((t, i) => html`<span class="argus-strip-tick" style=${`left:${i * 25}%`}>${day(t)}</span>`)}
      </span>
      ${opts.wide ? html`<span class="argus-strip-count"></span>` : nothing}
    </div>
    ${capped ? html`<p class="argus-lfaint">The newest ${result.episodes.length} of ${result.totals.episodes} episodes are drawn.</p>` : nothing}
  </div>`;
}
